export function isCertificationVersionMinor(minorVersion) {
  if (minorVersion === undefined || minorVersion === null) return false
  return String(minorVersion).trim() !== '0'
}

export const unchangeableFieldsForUpdates = [
  'certification_name',
  'certification_short_name',
  'name',
  'short_name',
  'difficulty',
  'learning_area_id',
  'learning_area_name',
  'voucher_start_date',
]

export const VOUCHER_KEYS = [
  'max_attempts',
  'time_after_first_attempt_in_minutes',
  'valid_for_in_days',
  'voucher_start_date',
]

export const PUBLISH_VALIDATION_FIELD_LABELS = {
  certification_short_name: 'Name',
  learning_area_id: 'Learning Area',
  difficulty: 'Difficulty',
  'voucher_config.max_attempts': '# of attempts',
  'voucher_config.valid_for_in_days': 'Valid for (days)',
  'voucher_config.time_after_first_attempt_in_minutes':
    'Next attempt time (hrs)',
  recommendations_md: 'Recommendations text',
  learning_paths: 'Learning path URL',
  accredible_group_id: 'Accredible group id',
  csv_hash: 'Exam Composition CSV',
  lab_id: 'Lab ID',
  exam_inner_instructions_md: 'Exam instructions text',
}

const PUBLISHED_STATUS = 'published'

export function adaptCertificationVersionToStore(payload = {}) {
  const data = payload.data || payload

  return {
    ...data,
    isMinor: isCertificationVersionMinor(data.minor_version),
  }
}

export function adaptCertificationVersionsListToStore(payload = {}) {
  const data = payload.data || payload

  return {
    ...data,
    results: (data.results || []).map((item) =>
      adaptCertificationVersionToStore(item)
    ),
  }
}

const hasRequiredValue = (value) => {
  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  return value !== null && value !== undefined
}

const hasRequiredPositiveNumber = (value) => {
  const number = Number(value)
  return Number.isFinite(number) && number > 0
}

export function getCertificationPublishValidationErrors(
  certificationVersion = {}
) {
  const certification = certificationVersion.certification || {}
  const voucherConfig = certificationVersion.voucher_config || {}
  const learningPaths = certificationVersion.learning_paths || []
  const certificationErrors = []

  if (!hasRequiredValue(certificationVersion.certification_short_name)) {
    certificationErrors.push('certification_short_name')
  }
  if (!hasRequiredValue(certification.learning_area_id)) {
    certificationErrors.push('learning_area_id')
  }
  if (!hasRequiredValue(certification.difficulty)) {
    certificationErrors.push('difficulty')
  }
  if (!hasRequiredPositiveNumber(voucherConfig.max_attempts)) {
    certificationErrors.push('voucher_config.max_attempts')
  }
  if (!hasRequiredPositiveNumber(voucherConfig.valid_for_in_days)) {
    certificationErrors.push('voucher_config.valid_for_in_days')
  }
  if (
    !hasRequiredPositiveNumber(
      voucherConfig.time_after_first_attempt_in_minutes
    )
  ) {
    certificationErrors.push(
      'voucher_config.time_after_first_attempt_in_minutes'
    )
  }
  if (!hasRequiredValue(certificationVersion.recommendations_md)) {
    certificationErrors.push('recommendations_md')
  }
  if (!Array.isArray(learningPaths) || !learningPaths.length) {
    certificationErrors.push('learning_paths')
  }
  if (!hasRequiredValue(certificationVersion.accredible_group_id)) {
    certificationErrors.push('accredible_group_id')
  }

  return certificationErrors
}

export function getExamPublishValidationErrors(certificationExam = {}) {
  const hasSelectedLabId = Boolean(
    (typeof certificationExam.questions_lab_id === 'string'
      ? certificationExam.questions_lab_id.trim()
      : certificationExam.questions_lab_id) ||
      (typeof certificationExam.practice_lab_id === 'string'
        ? certificationExam.practice_lab_id.trim()
        : certificationExam.practice_lab_id)
  )
  const examErrors = []

  if (!hasRequiredValue(certificationExam.csv_hash)) {
    examErrors.push('csv_hash')
  }
  if (!hasSelectedLabId) {
    examErrors.push('lab_id')
  }
  if (!hasRequiredValue(certificationExam.exam_inner_instructions_md)) {
    examErrors.push('exam_inner_instructions_md')
  }

  return examErrors
}

export function getPublishValidationErrors(
  certificationVersion = {},
  certificationExam = {}
) {
  const certificationErrors = getCertificationPublishValidationErrors(
    certificationVersion
  )
  const examErrors =
    certificationExam && certificationExam.status === PUBLISHED_STATUS
      ? getExamPublishValidationErrors(certificationExam)
      : []

  return {
    certification: certificationErrors,
    exam: examErrors,
  }
}

export function canPublishCertificationVersion(
  certificationVersion = {},
  certificationExam = {}
) {
  const errors = getPublishValidationErrors(
    certificationVersion,
    certificationExam
  )

  return !errors.certification.length && !errors.exam.length
}

export function mapPublishValidationErrorsToLabels(errors = []) {
  return errors.map(
    (fieldName) => PUBLISH_VALIDATION_FIELD_LABELS[fieldName] || fieldName
  )
}

const normalizeNumberValue = (value) => {
  if (value === undefined || value === null || value === '') return null
  const normalized = Number(value)
  return Number.isNaN(normalized) ? null : normalized
}

const normalizeEmptyValue = (value) => {
  if (value === undefined || value === null || value === '') return null
  return value
}

const normalizeComparableStringValue = (key, value) => {
  if (key !== 'recommendations_md' && key !== 'instructions_md') return value
  if (value === undefined || value === null) return ''
  return String(value).replace(/\r\n/g, '\n')
}

const normalizeDateValue = (value) => {
  if (value === undefined || value === null || value === '') return null

  const normalized = new Date(value)
  return Number.isNaN(normalized.getTime()) ? value : normalized.toISOString()
}

const normalizeAccredibleGroupId = (value) => {
  const normalized = normalizeEmptyValue(value)
  return normalized === null ? null : String(normalized)
}

// retires_at is a Django DateField: the wire format is "YYYY-MM-DD", and null
// clears the date. Deliberately not normalizeDateValue - toISOString() emits a
// full datetime that DateField rejects, and parsing a bare date string builds a
// UTC midnight that reads back a day earlier in negative-offset timezones.
const normalizeRetiresAtValue = (value) => {
  if (value === undefined || value === null || value === '') return null
  return String(value).slice(0, 10)
}

const normalizeLearningPathIds = (learningPaths) => {
  if (!Array.isArray(learningPaths)) return []

  return learningPaths.map((learningPath) =>
    learningPath && typeof learningPath === 'object'
      ? learningPath.id
      : learningPath
  )
}

const areArraysEqual = (left, right) => {
  if (left.length !== right.length) return false

  const sortedLeft = [...left].sort()
  const sortedRight = [...right].sort()

  return sortedLeft.every((value, index) => value === sortedRight[index])
}

const shouldExcludeField = (fieldNames = []) => {
  return fieldNames.some((fieldName) =>
    unchangeableFieldsForUpdates.includes(fieldName)
  )
}
export function adaptNewCertificationVersionToApi(params = {}) {
  const certificationVersionId =
    typeof params === 'string' ? params : params.sourceVersionId
  return {
    source_version_id: certificationVersionId,
  }
}

export function adaptPatchCertificationVersionToApi(params = {}) {
  const form = params.form || {}
  const source = params.certificationVersion || {}
  const sourceVoucherConfig = source.voucher_config || {}

  let payload = {}
  let voucherConfigChanged = false

  Object.keys(form).forEach((key) => {
    const isVoucherField = VOUCHER_KEYS.includes(key)
    const isDateField = key === 'voucher_start_date'

    if (shouldExcludeField([key])) return

    if (key === 'accredible_group_id') {
      const formValue = normalizeAccredibleGroupId(form[key])
      const sourceValue = normalizeAccredibleGroupId(source[key])

      if (formValue !== sourceValue) {
        payload[key] = normalizeEmptyValue(form[key])
      }
      return
    }

    if (key === 'retires_at') {
      const formValue = normalizeRetiresAtValue(form[key])
      const sourceValue = normalizeRetiresAtValue(source[key])

      if (formValue !== sourceValue) {
        payload[key] = formValue
      }
      return
    }

    if (key === 'learning_paths') {
      const formValue = normalizeLearningPathIds(form[key])
      const sourceValue = normalizeLearningPathIds(source[key])

      if (!areArraysEqual(formValue, sourceValue)) {
        payload[key] = formValue
      }
      return
    }

    if (isVoucherField) {
      const formValue = isDateField
        ? normalizeDateValue(form[key])
        : normalizeNumberValue(form[key])
      const sourceValue = isDateField
        ? normalizeDateValue(sourceVoucherConfig[key])
        : normalizeNumberValue(sourceVoucherConfig[key])

      if (formValue === sourceValue) return
      voucherConfigChanged = true
      return
    }

    const formValue = normalizeComparableStringValue(key, form[key])
    const sourceValue = normalizeComparableStringValue(key, source[key])

    if (formValue !== sourceValue) {
      payload[key] = normalizeEmptyValue(form[key])
    }
  })

  if (voucherConfigChanged) {
    const voucherConfigPayload = {}
    VOUCHER_KEYS.forEach((voucherKey) => {
      if (voucherKey === 'voucher_start_date') return
      voucherConfigPayload[voucherKey] = normalizeNumberValue(form[voucherKey])
    })

    const voucherStartDate = normalizeDateValue(form.voucher_start_date)
    if (voucherStartDate !== null) {
      voucherConfigPayload.voucher_start_date = voucherStartDate
    }
    payload['voucher_config'] = voucherConfigPayload
  }
  if (params.changeNote) {
    payload.change_note = params.changeNote
  }

  return payload
}
